import React, { createContext, useCallback, useContext, useState } from 'react';
import {
  FEATURE_FLAG_STORAGE_KEY,
  FEATURE_FLAGS,
  FeatureFlag,
  FeatureFlagOverrides,
  isFeatureFlagEnabled,
  readFeatureFlagOverrides,
} from './featureFlags';

type FeatureFlagContextValue = {
  isEnabled: (flag: FeatureFlag) => boolean;
  setEnabled: (flag: FeatureFlag, enabled: boolean) => void;
  storageError: boolean;
  overlayOpen: boolean;
  openOverlay: () => void;
  closeOverlay: () => void;
  toggleOverlay: () => void;
};

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

export function FeatureFlagProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [overrides, setOverrides] = useState<FeatureFlagOverrides>(() =>
    readFeatureFlagOverrides(window.localStorage)
  );
  const [storageError, setStorageError] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  function isEnabled(flag: FeatureFlag): boolean {
    return isFeatureFlagEnabled(FEATURE_FLAGS[flag].status, overrides[flag]);
  }

  function setEnabled(flag: FeatureFlag, enabled: boolean): void {
    const next = { ...overrides, [flag]: enabled };
    setOverrides(next);
    try {
      window.localStorage.setItem(FEATURE_FLAG_STORAGE_KEY, JSON.stringify(next));
      setStorageError(false);
    } catch (error) {
      console.error('Could not save feature flag choice', error);
      setStorageError(true);
    }
  }

  const openOverlay = useCallback((): void => {
    setOverlayOpen(true);
  }, []);

  const closeOverlay = useCallback((): void => {
    setOverlayOpen(false);
  }, []);

  const toggleOverlay = useCallback((): void => {
    setOverlayOpen((current) => !current);
  }, []);

  return (
    <FeatureFlagContext.Provider
      value={{
        isEnabled,
        setEnabled,
        storageError,
        overlayOpen,
        openOverlay,
        closeOverlay,
        toggleOverlay,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlags(): FeatureFlagContextValue {
  const context = useContext(FeatureFlagContext);
  if (!context) throw new Error('useFeatureFlags must be used within FeatureFlagProvider');
  return context;
}
